// src/pages/AppAdsPage.jsx
//
// app-ads.txt verification with zero seller-account access. The developer
// publishes app-ads.txt on their PUBLIC website (no credentials needed to
// read it, unlike a GAM/Prebid/APS seller console) declaring which ad
// systems are authorized to sell their app's inventory. This page fetches
// that file off the proxy (GET /proxy/appads-check?domain=...), parses it,
// and reconciles it against the ad-system domains ACTUALLY observed bidding
// in captured traffic — surfacing anything bidding that ISN'T authorized.
import React, { useState } from "react";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

const PROXY_API_BASE = (process.env.REACT_APP_PROXY_API_URL || "/proxy").replace(/\/$/, "");

/**
 * normalizeDomain(input) -> bare hostname, or "" when there is nothing usable.
 *
 * The proxy requires a BARE hostname and rejects anything else as
 * invalid_domain — deliberately, so this route cannot be used as an open
 * proxy (appAdsCheck.js). That guard is right and stays; the input is what
 * should be forgiving. Pasting "https://www.foxnews.com/ads.txt" is the
 * natural thing to do and used to fail with a bare "invalid_domain" and no
 * hint about what it wanted.
 *
 * Handles scheme, path, query, port and a stray trailing dot. Does NOT strip
 * `www.`: app-ads.txt is served from the domain the store listing declares,
 * and www.example.com and example.com can genuinely differ — silently
 * rewriting one to the other would produce a confident wrong answer.
 */
export function normalizeDomain(input) {
    const raw = String(input || "").trim();
    if (!raw) return "";
    let host = raw;
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) {
        try { host = new URL(raw).hostname; } catch { return ""; }
    } else {
        // No scheme: cut at the first path/query separator.
        host = raw.split(/[/?#]/)[0];
    }
    return host.replace(/:\d+$/, "").replace(/\.$/, "").toLowerCase();
}

async function checkAppAds(domain) {
    const res = await fetch(`${PROXY_API_BASE}/proxy/appads-check?domain=${encodeURIComponent(domain)}`);
    let json = null;
    try { json = await res.json(); } catch { /* empty/non-JSON body */ }
    if (!res.ok) {
        const err = new Error((json && (json.detail || json.error)) || `Request failed (${res.status})`);
        err.status = res.status;
        err.body = json;
        throw err;
    }
    return json;
}

function Chevron({ open }) {
    return (
        <span style={{ display: "inline-block", transition: "transform 0.15s", transform: open ? "rotate(90deg)" : "rotate(0deg)" }}>
            ▶
        </span>
    );
}

export default function AppAdsPage() {
    const [domain, setDomain] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [invalidOpen, setInvalidOpen] = useState(false);

    const runCheck = async () => {
        const trimmed = normalizeDomain(domain);
        if (!trimmed) {
            setError("Enter a developer site domain (e.g. foxnews.com).");
            return;
        }
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const data = await checkAppAds(trimmed);
            // Echo the normalised host: when a pasted URL is trimmed to a
            // hostname, the result must not look like it checked what was typed.
            setResult({ ...data, checkedDomain: trimmed });
        } catch (e) {
            setError(e.message || "Request failed");
        } finally {
            setLoading(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter") runCheck();
    };

    const toolFailed = result && result.ok === false;
    const invalidLines = result?.parsed?.invalidLines || [];
    const matches = result?.reconcile?.matches || [];
    const missing = result?.reconcile?.missing || [];
    const unused = result?.reconcile?.unused ?? 0;

    return (
        <div style={{ padding: 20, display: "grid", gap: 16, maxWidth: 1100, margin: "0 auto" }}>
            <div>
                <h2 style={{ margin: 0, color: "var(--ink)" }}>app-ads.txt Check</h2>
                <p style={{ margin: "4px 0 0 0", color: "var(--muted)", fontSize: 13 }}>
                    Fetches the developer&rsquo;s <b>public</b> app-ads.txt (no seller-account access needed),
                    parses the declared ad systems, and reconciles them against the ad-system domains
                    actually observed bidding in captured traffic.
                </p>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                    type="text"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="foxnews.com — a full URL is fine, it will be trimmed to the host"
                    style={{
                        flex: 1,
                        fontSize: 14,
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: "1px solid var(--border)",
                        background: "var(--card)",
                        color: "var(--ink)",
                    }}
                />
                <button
                    onClick={runCheck}
                    disabled={loading}
                    style={{
                        fontSize: 13,
                        padding: "10px 20px",
                        borderRadius: 8,
                        border: "none",
                        background: loading ? "var(--muted)" : "var(--primary, #3b82f6)",
                        color: "#fff",
                        cursor: loading ? "default" : "pointer",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                    }}
                >
                    {loading ? "Checking…" : "Check"}
                </button>
            </div>

            {error && (
                <div style={{ padding: "10px 14px", borderRadius: 8, background: "#ef444411", border: "1px solid #ef444455", color: "#ef4444", fontSize: 13 }}>
                    {error}
                </div>
            )}

            {toolFailed && (
                <div style={{ padding: "10px 14px", borderRadius: 8, background: "#ef444411", border: "1px solid #ef444455", color: "#ef4444", fontSize: 13 }}>
                    Could not fetch app-ads.txt: {result.error}
                </div>
            )}

            {result && result.ok && (
                <>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>
                        Source: <a href={result.source?.url} target="_blank" rel="noreferrer" style={{ color: "var(--ink)" }}>{result.source?.url}</a>
                        {" "}&middot; HTTP {result.source?.status}
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <Chip label={`${result.parsed?.entryCount ?? 0} entries`} variant="outlined" />
                        <Chip label={`${result.observedCount ?? 0} observed sources`} variant="outlined" />
                        <Chip label={`${matches.length} matches`} color="success" variant={matches.length ? "filled" : "outlined"} sx={{ fontWeight: 700 }} />
                        <Chip label={`${missing.length} MISSING`} color="error" variant={missing.length ? "filled" : "outlined"} sx={{ fontWeight: 700 }} />
                    </div>

                    {result.observedCount === 0 && (
                        <div style={{ padding: "10px 14px", borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)", color: "var(--muted)", fontSize: 13 }}>
                            No captured traffic to reconcile — the parse above still shows the file&rsquo;s contents.
                        </div>
                    )}

                    {missing.length > 0 && (
                        <div style={{ borderRadius: 10, border: "1px solid #ef444455", background: "#ef444411", padding: 14 }}>
                            <div style={{ fontWeight: 700, color: "#ef4444", marginBottom: 8, fontSize: 14 }}>
                                Missing — observed bidding but NOT authorized in app-ads.txt
                            </div>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Domain</TableCell>
                                            <TableCell>Via</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {missing.map((m) => (
                                            <TableRow key={m.domain}>
                                                <TableCell style={{ color: "#ef4444", fontWeight: 600 }}>{m.domain}</TableCell>
                                                <TableCell>{m.via}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    )}

                    <div>
                        <div style={{ fontWeight: 700, color: "var(--ink)", marginBottom: 8, fontSize: 14 }}>
                            Matches — observed &amp; authorized
                        </div>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Domain</TableCell>
                                        <TableCell>Seller IDs</TableCell>
                                        <TableCell>Relationship(s)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {matches.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} style={{ color: "var(--muted)" }}>No matches.</TableCell>
                                        </TableRow>
                                    )}
                                    {matches.map((m) => (
                                        <TableRow key={m.domain}>
                                            <TableCell>{m.domain}</TableCell>
                                            <TableCell>{m.sellerIds}</TableCell>
                                            <TableCell>{(m.relationships || []).join(", ")}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>

                    <div>
                        <div
                            onClick={() => setInvalidOpen((v) => !v)}
                            style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, color: "var(--muted)" }}
                        >
                            <IconButton size="small" style={{ padding: 2 }}>
                                <Chevron open={invalidOpen} />
                            </IconButton>
                            Invalid lines ({invalidLines.length})
                        </div>
                        <Collapse in={invalidOpen} timeout="auto" unmountOnExit>
                            <TableContainer style={{ marginTop: 8 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Line</TableCell>
                                            <TableCell>Text</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {invalidLines.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={2} style={{ color: "var(--muted)" }}>None.</TableCell>
                                            </TableRow>
                                        )}
                                        {invalidLines.map((l) => (
                                            <TableRow key={l.line}>
                                                <TableCell>{l.line}</TableCell>
                                                <TableCell style={{ fontFamily: "monospace", fontSize: 12 }}>{l.text}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Collapse>
                    </div>

                    <div style={{ fontSize: 12, color: "var(--muted)" }}>
                        {unused} authorized domain(s) never observed in this session.
                    </div>
                </>
            )}
        </div>
    );
}
